import React from 'react';
import { Settings2, Tablet, Copy, Check } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

interface EmployeeOption {
    id: string;
    first_name: string;
    last_name: string;
}

const employeeName = (r: { first_name?: string; last_name?: string }) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Salarié';

export const TimeClockSettings: React.FC<{ employees: EmployeeOption[] }> = ({ employees }) => {
    const { user } = useAuth();
    const isRestauration = user?.client_type === 'restauration';

    const [settingsEnabled, setSettingsEnabled] = React.useState(true);
    const [settingsLoading, setSettingsLoading] = React.useState(true);
    const [settingsSaving, setSettingsSaving] = React.useState(false);
    const [kioskCode, setKioskCode] = React.useState('');
    const [kioskCodeDraft, setKioskCodeDraft] = React.useState('');
    const [kioskSaving, setKioskSaving] = React.useState(false);
    const [kioskCopied, setKioskCopied] = React.useState(false);
    const [employeesWithPin, setEmployeesWithPin] = React.useState<string[]>([]);
    const [resendingId, setResendingId] = React.useState<string | null>(null);
    const [resendFeedback, setResendFeedback] = React.useState<Record<string, string>>({});

    React.useEffect(() => {
        moduleApi.getTimeClockSettings().then((d: any) => {
            setSettingsEnabled(d?.enabled !== false);
            setKioskCode(d?.kiosk_code || '');
            setKioskCodeDraft(d?.kiosk_code || '');
            setEmployeesWithPin(Array.isArray(d?.employees_with_pin) ? d.employees_with_pin : []);
        }).catch(() => {}).finally(() => setSettingsLoading(false));
    }, []);

    const saveSettings = async (enabled: boolean) => {
        setSettingsEnabled(enabled);
        setSettingsSaving(true);
        try {
            await moduleApi.saveTimeClockSettings({ enabled });
        } finally {
            setSettingsSaving(false);
        }
    };

    const saveKioskCode = async () => {
        const isFirstActivation = !kioskCode && !!kioskCodeDraft.trim();
        if (isFirstActivation && !confirm(
            "En activant le mode kiosque, chaque salarié actif recevra automatiquement un email avec un code PIN personnel à 4 chiffres. Ce code sert uniquement à pointer ses heures sur la tablette, à rien d'autre. Continuer ?"
        )) return;
        setKioskSaving(true);
        try {
            const res: any = await moduleApi.saveTimeClockSettings({ kiosk_code: kioskCodeDraft.trim() });
            setKioskCode(kioskCodeDraft.trim());
            if (isFirstActivation) {
                const d: any = await moduleApi.getTimeClockSettings();
                setEmployeesWithPin(Array.isArray(d?.employees_with_pin) ? d.employees_with_pin : []);
                if (res?.pins_sent) alert(`${res.pins_sent} salarié(s) ont reçu leur code par email.`);
            }
        } finally {
            setKioskSaving(false);
        }
    };

    const kioskClientId = user?.type === 'collaborator' ? (user?.client_id || (user as any)?.clientId) : user?.id;
    const kioskUrl = kioskCode ? `${window.location.origin}/pointage-kiosque/${kioskClientId || ''}` : '';
    const handleCopyKioskUrl = async () => {
        try {
            await navigator.clipboard.writeText(kioskUrl);
            setKioskCopied(true);
            setTimeout(() => setKioskCopied(false), 2000);
        } catch {}
    };

    const handleResendPin = async (employeeId: string) => {
        setResendingId(employeeId);
        setResendFeedback((prev) => ({ ...prev, [employeeId]: '' }));
        try {
            const res: any = await moduleApi.resendEmployeeKioskPin(employeeId);
            if (res?.error) { setResendFeedback((prev) => ({ ...prev, [employeeId]: res.error })); return; }
            setEmployeesWithPin((prev) => prev.includes(employeeId) ? prev : [...prev, employeeId]);
            setResendFeedback((prev) => ({ ...prev, [employeeId]: `Envoyé à ${res?.email || 'leur email'}` }));
            setTimeout(() => setResendFeedback((prev) => ({ ...prev, [employeeId]: '' })), 4000);
        } catch (e: any) {
            setResendFeedback((prev) => ({ ...prev, [employeeId]: e?.message || 'Erreur' }));
        } finally {
            setResendingId(null);
        }
    };

    if (!isRestauration) return null;

    return (
        <div className="max-w-2xl space-y-6">
            <header>
                <h1 className="text-2xl font-bold tracking-tight text-content flex items-center gap-2"><Settings2 size={20} /> Réglages du pointage</h1>
                <p className="text-content-muted mt-1 text-sm">Le suivi des pointages au quotidien se fait depuis Planning &amp; agenda &gt; Pointage.</p>
            </header>

            {!settingsLoading && (
                <div className="p-4 rounded-2xl bg-surface-soft border border-border flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-content flex items-center gap-2"><Settings2 size={15} /> Pointage activé</p>
                        <p className="text-xs text-content-muted mt-0.5">Si désactivé, vos salariés n'ont plus le bouton "Je commence mon service" — pratique si vous préférez rester sur votre feuille de pointage papier (Planning &gt; Exporter jour).</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                        <input type="checkbox" checked={settingsEnabled} className="sr-only peer" disabled={settingsSaving}
                            onChange={(e) => saveSettings(e.target.checked)} />
                        <div className="w-10 h-6 bg-surface rounded-full border border-border peer-checked:bg-brand-600 transition-all after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4" />
                    </label>
                </div>
            )}

            {!settingsLoading && (
                <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-4">
                    <div>
                        <p className="text-sm font-bold text-content flex items-center gap-2"><Tablet size={15} /> Mode kiosque (tablette partagée)</p>
                        <p className="text-xs text-content-muted mt-0.5">Installez une tablette au vestiaire ou en cuisine : chaque salarié pointe en tapant son code PIN à 4 chiffres, sans avoir besoin de se connecter avec son compte.</p>
                    </div>

                    <div className="flex flex-wrap items-end gap-3">
                        <div>
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1 block">Code d'accès de la tablette</label>
                            <input type="text" value={kioskCodeDraft} onChange={(e) => setKioskCodeDraft(e.target.value)}
                                placeholder="ex: cuisine2026" className="bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 w-48" />
                        </div>
                        <button onClick={saveKioskCode} disabled={kioskSaving || !kioskCodeDraft.trim()} className="px-3.5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all disabled:opacity-50">
                            {kioskSaving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>

                    {kioskCode && (
                        <div className="flex flex-wrap items-center gap-2">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Adresse à ouvrir sur la tablette</label>
                            <code className="text-xs bg-surface border border-border rounded-lg px-2.5 py-1.5 text-content break-all">{kioskUrl}</code>
                            <button onClick={handleCopyKioskUrl} className="p-2 rounded-lg text-content-muted hover:text-content hover:bg-surface transition-colors" title="Copier le lien">
                                {kioskCopied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                            </button>
                        </div>
                    )}

                    {kioskCode && employees.length > 0 && (
                        <div className="pt-2 border-t border-border">
                            <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-2">Codes PIN des salariés</p>
                            <p className="text-[11px] text-content-muted mb-2">Le code est généré et envoyé par email automatiquement — vous ne pouvez que le renvoyer si un salarié l'a perdu.</p>
                            <div className="space-y-2">
                                {employees.map((e) => {
                                    const hasPin = employeesWithPin.includes(e.id);
                                    const feedback = resendFeedback[e.id];
                                    return (
                                        <div key={e.id} className="flex items-center gap-2">
                                            <span className="text-sm text-content flex-1 min-w-0 truncate">{employeeName(e)}</span>
                                            {feedback && <span className="text-[11px] text-content-muted shrink-0">{feedback}</span>}
                                            <span className={`text-[10px] font-bold shrink-0 ${hasPin ? 'text-emerald-500' : 'text-content-muted'}`}>
                                                {hasPin ? 'Configuré' : 'Pas encore'}
                                            </span>
                                            <button onClick={() => handleResendPin(e.id)} disabled={resendingId === e.id}
                                                className="px-3 py-1.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface text-xs font-bold transition-all disabled:opacity-50 shrink-0">
                                                {resendingId === e.id ? '...' : hasPin ? 'Renvoyer' : 'Envoyer'}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TimeClockSettings;
